import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../../i18n";
import { LOGO_IMG } from "../../data";

const linkIds = ["work", "story", "services", "book"];
const LANGS = ["en", "pt", "es"];

export const Nav = ({ onNavigate }) => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); onNavigate(id); };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#F9F6F0]/90 backdrop-blur-md border-b border-[#2D2A26]/10" : "bg-transparent"
      }`}
    >
      <nav className="flex items-center justify-between px-6 md:px-12 h-20">
        <button data-testid="nav-brand" onClick={() => go("top")} className="flex items-center gap-3">
          <img src={LOGO_IMG} alt="Lilah C. Photography" className="h-12 md:h-16 w-auto object-contain" />
        </button>

        <div className="hidden md:flex items-center gap-10">
          {linkIds.map((id) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)} className="overline text-[0.68rem] relative group">
              {t.nav[id]}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#E2725B] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}

          <div className="flex items-center gap-2 pl-2 border-l border-[#2D2A26]/15">
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`overline text-[0.6rem] transition-colors ${lang === l ? "text-[#E2725B]" : "text-[#2D2A26]/40 hover:text-[#2D2A26]"}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <button data-testid="nav-book-btn" onClick={() => go("book")} className="px-6 py-2.5 bg-[#2D2A26] text-[#F9F6F0] overline text-[0.62rem] rounded-full hover:bg-[#E2725B] transition-colors duration-300">
            {t.nav.cta}
          </button>
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden overline text-xs">
          {open ? "×" : "Menu"}
        </button>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-[#F9F6F0] border-t border-[#2D2A26]/10 px-6 py-6 flex flex-col gap-5">
          {linkIds.map((id) => (
            <button key={id} onClick={() => go(id)} className="font-serif-display text-3xl italic text-left">{t.nav[id]}</button>
          ))}
          <div className="flex gap-4 pt-2">
            {LANGS.map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`overline text-xs ${lang === l ? "text-[#E2725B]" : "text-[#2D2A26]/40"}`}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>
      )}
    </motion.header>
  );
};
